import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Sparkles, XCircle, Timer, Minimize2, Brain } from "lucide-react";
import { useBranding } from "../lib/branding";

/**
 * Format a milliseconds-remaining number into a friendly ETA blurb.
 * Under 60s → "<1 min", under an hour → "~X min", otherwise "~Xh Ym".
 */
function formatEta(ms) {
    if (!Number.isFinite(ms) || ms <= 0) return "almost done";
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) return "<1 min left";
    const totalMin = Math.round(totalSec / 60);
    if (totalMin < 60) return `~${totalMin} min left`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m ? `~${h}h ${m}m left` : `~${h}h left`;
}

/**
 * Compact popup shown while an AI analysis/estimate is in-flight.
 * Two progress bars:
 *   * Blue (top): synthetic overall pacing — drifts smoothly to ~95% so the
 *     user sees continuous motion during long AI calls.
 *   * Green (bottom): live per-step progress driven by the backend job's
 *     `stage` + `progress_pct` fields. Tells the user WHAT the AI is doing
 *     right now (loading docs, extracting text, calling the model, saving…).
 *
 * Supports an optional Cancel / Abort button (when ``onCancel`` is wired).
 * Tapping it opens a confirm dialog — AI calls usually cost money and mid-run
 * cancels are destructive (no partial results saved).
 */
export default function AnalyzingOverlay({ open, kind = "analyze", stage = "", stagePct = null, usage = null, pagesScanned = null, pagesTotal = null, onCancel, canCancel = false }) {
    const [progress, setProgress] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // iter-209 — minimize-to-floating-bubble so the user can keep working.
    // State is local to this component, so when the parent closes the
    // overlay (job finished / canceled), minimized resets automatically.
    const [minimized, setMinimized] = useState(false);
    useEffect(() => { if (!open) setMinimized(false); }, [open]);
    // iter-62: superadmin-uploaded hero videos take precedence over the
    // bundled default. We resolve the URL through REACT_APP_BACKEND_URL
    // so dev + prod both work without a separate env var.
    const { videoAnalyzeUrl, videoEstimateUrl } = useBranding();
    const slotUrl =
        kind === "estimate" ? videoEstimateUrl : videoAnalyzeUrl;
    const heroVideoSrc = slotUrl
        ? slotUrl.startsWith("http")
            ? slotUrl
            : `${process.env.REACT_APP_BACKEND_URL}${slotUrl}`
        : "/uploading.mp4";

    useEffect(() => {
        if (!open) {
            setProgress(0);
            setConfirmOpen(false);
            return;
        }
        setProgress(2);
        // Slow, paced drift so the bar tracks the real work instead of
        // racing to 95% and pinning there. Tuned for typical 30–90s runs
        // on medium-to-large projects.
        const id = setInterval(() => {
            setProgress((p) => {
                if (p < 40) return p + 0.2;    // ~0.8%/sec
                if (p < 70) return p + 0.08;   // ~0.3%/sec
                if (p < 90) return p + 0.03;   // ~0.12%/sec
                if (p < 95) return p + 0.01;   // ~0.04%/sec
                return p;
            });
        }, 250);
        return () => clearInterval(id);
    }, [open]);

    // Rolling play-by-play log of stage strings. Every new distinct
    // ``stage`` we see gets appended with a timestamp so the user can
    // watch the job's commentary instead of staring at a frozen line.
    const [log, setLog] = React.useState([]);
    const lastStageRef = React.useRef("");
    React.useEffect(() => {
        if (!stage) return;
        if (stage === lastStageRef.current) return;
        lastStageRef.current = stage;
        const at = new Date().toLocaleTimeString(undefined, {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        setLog((prev) => [...prev.slice(-14), { t: at, msg: stage }]);
    }, [stage]);
    React.useEffect(() => {
        if (!open) {
            setLog([]);
            lastStageRef.current = "";
        }
    }, [open]);

    // iter-110: rolling ETA. We keep a small window of recent
    // (timestamp, progress_pct) samples, take the average rate over
    // the last ~60 seconds, and project the remaining % out. Only
    // published once we have enough signal so the number doesn't jump
    // wildly in the first seconds of a run.
    const [etaText, setEtaText] = React.useState("");
    const etaSamplesRef = React.useRef([]);
    React.useEffect(() => {
        if (typeof stagePct !== "number") return;
        const now = Date.now();
        const samples = etaSamplesRef.current;
        samples.push({ t: now, pct: stagePct });
        // Keep only the last 60s of history (max 60 points at 1/s).
        while (samples.length > 60) samples.shift();
        while (samples.length > 2 && now - samples[0].t > 60_000) samples.shift();

        if (stagePct >= 99.5) {
            setEtaText("almost done");
            return;
        }
        // Need at least ~8s of data AND meaningful pct movement to
        // avoid absurd early estimates.
        if (samples.length < 4) return;
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dPct = last.pct - first.pct;
        const dMs = last.t - first.t;
        if (dPct <= 0.05 || dMs < 8_000) return;
        const rate = dPct / dMs; // pct per ms
        const remaining = 100 - stagePct;
        const etaMs = remaining / rate;
        setEtaText(formatEta(etaMs));
    }, [stagePct]);
    React.useEffect(() => {
        if (!open) {
            etaSamplesRef.current = [];
            setEtaText("");
        }
    }, [open]);

    const titleUpper = (
        kind === "estimate" ? "Estimating"
        : kind === "takeoff" ? "Taking off"
        : "Analyzing"
    ).toUpperCase();

    return (
        <>
            {open && minimized && (
                <FloatingBubble
                    title={titleUpper}
                    progress={progress}
                    stage={stage}
                    stagePct={stagePct}
                    onRestore={() => setMinimized(false)}
                />
            )}
            <Dialog open={open && !minimized}>
                <DialogContent
                    className="!bg-[#0a0a0a] border border-white/15 text-white p-0 overflow-hidden w-[calc(100vw-1rem)] max-w-[420px] max-h-[85vh] rounded-lg [&>button]:hidden flex flex-col"
                    data-testid="analyzing-overlay"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    <VisuallyHidden.Root>
                        <DialogTitle>{titleUpper}</DialogTitle>
                        <DialogDescription>
                            Reading your drawings and extracting scope.
                        </DialogDescription>
                    </VisuallyHidden.Root>

                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/15 bg-[#111] shrink-0">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#8fa4f0]" />
                            <span className="text-sm font-bold uppercase tracking-[0.14em] text-white" data-testid="analyzing-title">
                                {titleUpper}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => setMinimized(true)}
                                data-testid="analyzing-minimize-btn"
                                aria-label="Minimize to floating bubble"
                                title="Minimize — keep working while this runs"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold text-slate-200 hover:text-white hover:bg-[#1a3fd1]/20 border border-white/15 hover:border-[#1a3fd1]/60 transition-colors"
                            >
                                <Minimize2 className="w-3 h-3" />
                                Minimize
                            </button>
                            {canCancel && (
                                <button
                                    type="button"
                                    onClick={() => setConfirmOpen(true)}
                                    data-testid="analyzing-cancel-btn"
                                    aria-label="Cancel AI job"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold text-rose-300 hover:text-white hover:bg-rose-500/20 border border-rose-500/40 transition-colors"
                                >
                                    <XCircle className="w-3 h-3" />
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Video block: capped at a fraction of the viewport on
                        mobile so the whole modal stays inside the visible
                        window even on short phones with the address bar out. */}
                    <div className="relative bg-black aspect-video max-h-[45vh] shrink-0">
                        <video
                            key={heroVideoSrc}
                            src={heroVideoSrc}
                            className="absolute inset-0 w-full h-full object-cover"
                            muted
                            autoPlay
                            loop
                            playsInline
                            preload="metadata"
                            data-testid="analyzing-video"
                        />
                    </div>

                    <div className="px-4 py-3 border-t border-white/15 bg-[#111] space-y-2 shrink-0">
                        {/* Top bar: overall drift */}
                        <div className="h-1.5 w-full rounded-full bg-[#0a0a0a] overflow-hidden border border-white/15">
                            <div
                                className="h-full bg-[#1a3fd1] transition-[width] duration-200"
                                style={{ width: `${progress}%` }}
                                data-testid="analyzing-progress-bar"
                            />
                        </div>
                        {/* Green bar: per-step, backend-driven */}
                        <div className="h-1.5 w-full rounded-full bg-[#0a0a0a] overflow-hidden border border-white/15">
                            <div
                                className="h-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.45)] transition-[width] duration-300"
                                style={{
                                    width: `${
                                        typeof pagesScanned === "number" && typeof pagesTotal === "number" && pagesTotal > 0
                                            ? Math.max(0, Math.min(100, (pagesScanned / pagesTotal) * 100))
                                            : typeof stagePct === "number"
                                                ? Math.max(0, Math.min(100, stagePct))
                                                : 0
                                    }%`,
                                }}
                                data-testid="analyzing-stage-bar"
                            />
                        </div>
                        {/* iter-214 — page-scan counter. Lights up during
                            the extraction phase so users can see concrete
                            progress on big tenders ("412 / 2041 pages
                            scanned"). Hidden once extraction finishes
                            and the LLM takes over. */}
                        {typeof pagesTotal === "number" && pagesTotal > 0 && (
                            <div
                                className="flex items-center justify-between gap-3 rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2"
                                data-testid="analyzing-pages-counter"
                            >
                                <div className="flex flex-col leading-tight">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">
                                        Pages Scanned
                                    </span>
                                    <span className="text-base font-black tabular-nums text-white">
                                        <span className="text-[#8fa4f0]">
                                            {typeof pagesScanned === "number" ? pagesScanned.toLocaleString() : "0"}
                                        </span>
                                        <span className="text-slate-400 mx-1">/</span>
                                        <span>{pagesTotal.toLocaleString()}</span>
                                    </span>
                                </div>
                                <div className="text-right leading-tight">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">
                                        Scan %
                                    </span>
                                    <div className="text-base font-black tabular-nums text-emerald-300">
                                        {typeof pagesScanned === "number"
                                            ? `${Math.round((pagesScanned / pagesTotal) * 100)}%`
                                            : "0%"}
                                    </div>
                                </div>
                            </div>
                        )}
                        <p
                            className="text-[11px] text-emerald-200 font-semibold leading-relaxed min-h-[1em] truncate drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
                            data-testid="analyzing-stage-label"
                            title={stage}
                        >
                            {stage || "Reading drawings and extracting scope…"}
                        </p>
                        {/* iter-110: live ETA next to the stage label. Only
                            renders once we have enough poll history to make
                            a stable projection. */}
                        {etaText && (
                            <p
                                className="text-[11px] text-[#8fa4f0] flex items-center gap-1 leading-tight"
                                data-testid="analyzing-eta"
                            >
                                <Timer className="w-3 h-3" />
                                {etaText}
                            </p>
                        )}
                        {/* iter-206 — tell the user they can walk away.
                            iter-209 — bumped contrast; the overlay sometimes
                            renders on a light shadcn backdrop so we need text
                            that reads on both. */}
                        <p
                            className="text-[10px] text-emerald-200 font-semibold leading-snug drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
                            data-testid="analyzing-safe-to-close"
                        >
                            ✓ Safe to close this app — the estimate keeps running on the server. Reopen anytime to see progress or the finished result.
                        </p>
                        {/* Play-by-play log — keeps the last ~8 stage
                            transitions visible so the user never thinks
                            the job is hung. Scrolls bottom-up. */}
                        {log.length > 0 && (
                            <div
                                className="mt-1 max-h-24 overflow-y-auto rounded-sm border border-white/10 bg-[#111] px-2 py-1 space-y-[2px]"
                                data-testid="analyzing-log"
                            >
                                {log.slice(-8).map((entry, i) => (
                                    <div
                                        key={`${entry.t}-${i}`}
                                        className="flex items-start gap-2 text-[10px] leading-snug"
                                    >
                                        <span className="text-slate-500 tabular-nums shrink-0">
                                            {entry.t}
                                        </span>
                                        <span
                                            className={
                                                i === log.slice(-8).length - 1
                                                    ? "text-emerald-200 font-semibold"
                                                    : "text-slate-300"
                                            }
                                        >
                                            {entry.msg}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent className="bg-[#111] border-white/15">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Cancel this AI job?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            The job will stop immediately and no partial
                            results will be saved. You can start a fresh run
                            afterwards.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="analyzing-cancel-keep-running"
                            className="bg-[#1a1a1a] border-white/15 text-slate-200"
                        >
                            Keep running
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setConfirmOpen(false);
                                onCancel && onCancel();
                            }}
                            data-testid="analyzing-cancel-confirm"
                            className="bg-rose-500 hover:bg-rose-600 text-white"
                        >
                            Yes, cancel
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}


/**
 * iter-209 — Floating draggable bubble shown when the user minimizes an
 * in-flight AI run. Behaviour:
 *   • Pinned to bottom-right by default (position persisted in
 *     localStorage so it remembers across sessions).
 *   • Drag with mouse or touch.
 *   • Tap (no drag) restores the full overlay.
 *   • Shows live progress ring + percentage so the user always knows
 *     where things stand without opening the dialog.
 *   • Stays on top of everything via a high z-index, but doesn't block
 *     the rest of the UI — the user can continue working.
 */
function FloatingBubble({ title, progress, stage, stagePct, onRestore }) {
    const [pos, setPos] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem("pf.aiBubble.pos") || "null");
            if (stored && Number.isFinite(stored.x) && Number.isFinite(stored.y)) return stored;
        } catch { /* noop */ }
        // Default: 24px from the right, 24px from bottom. We compute on
        // first render because window may be undefined on SSR.
        const w = typeof window !== "undefined" ? window.innerWidth : 1024;
        const h = typeof window !== "undefined" ? window.innerHeight : 768;
        return { x: Math.max(16, w - 96 - 24), y: Math.max(16, h - 96 - 24) };
    });
    const dragStart = useRef(null);
    const didDrag = useRef(false);

    useEffect(() => {
        try { localStorage.setItem("pf.aiBubble.pos", JSON.stringify(pos)); } catch { /* noop */ }
    }, [pos]);

    // Clamp on viewport resize so the bubble never slips off-screen.
    useEffect(() => {
        const clamp = () => {
            setPos((p) => {
                const w = window.innerWidth;
                const h = window.innerHeight;
                return {
                    x: Math.min(Math.max(0, p.x), Math.max(0, w - 96)),
                    y: Math.min(Math.max(0, p.y), Math.max(0, h - 96)),
                };
            });
        };
        window.addEventListener("resize", clamp);
        return () => window.removeEventListener("resize", clamp);
    }, []);

    const onPointerDown = (e) => {
        e.target.setPointerCapture?.(e.pointerId);
        dragStart.current = { x: e.clientX, y: e.clientY, bx: pos.x, by: pos.y };
        didDrag.current = false;
    };
    const onPointerMove = (e) => {
        if (!dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (!didDrag.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
            didDrag.current = true;
        }
        if (didDrag.current) {
            const w = window.innerWidth;
            const h = window.innerHeight;
            setPos({
                x: Math.min(Math.max(0, dragStart.current.bx + dx), w - 96),
                y: Math.min(Math.max(0, dragStart.current.by + dy), h - 96),
            });
        }
    };
    const onPointerUp = (e) => {
        e.target.releasePointerCapture?.(e.pointerId);
        const wasDrag = didDrag.current;
        dragStart.current = null;
        didDrag.current = false;
        if (!wasDrag) onRestore();
    };

    // Prefer the backend-driven per-step pct for the ring (more honest
    // than the synthetic drifting progress). Fall back to synthetic.
    const pct = typeof stagePct === "number" ? Math.max(0, Math.min(100, stagePct)) : progress;
    const radius = 42;
    const circ = 2 * Math.PI * radius;
    const dash = (circ * pct) / 100;

    return (
        <div
            data-testid="ai-bubble"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            title={stage || `${title} in progress — tap to open`}
            style={{
                left: pos.x,
                top: pos.y,
                touchAction: "none",
            }}
            className="fixed z-[9999] w-24 h-24 rounded-full bg-[#0a0a0a] border-2 border-[#1a3fd1]/50 shadow-[0_8px_32px_rgba(0,163,255,0.4),0_0_0_1px_rgba(255,255,255,0.08)] cursor-grab active:cursor-grabbing select-none flex items-center justify-center"
        >
            {/* Progress ring */}
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 100 100">
                <circle
                    cx="50" cy="50" r={radius}
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="4"
                />
                <circle
                    cx="50" cy="50" r={radius}
                    fill="none"
                    stroke="#00A3FF"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ - dash}`}
                    style={{ transition: "stroke-dasharray 320ms ease-out" }}
                />
            </svg>
            <div className="flex flex-col items-center justify-center pointer-events-none">
                <Brain className="w-6 h-6 text-[#8fa4f0] drop-shadow-[0_0_6px_rgba(0,163,255,0.6)]" />
                <span className="text-[11px] font-bold text-white tabular-nums leading-tight mt-0.5">
                    {Math.round(pct)}%
                </span>
            </div>
            {/* Subtle pulse */}
            <span className="absolute inset-0 rounded-full border-2 border-[#1a3fd1]/40 animate-ping opacity-40 pointer-events-none" />
        </div>
    );
}

